// components/EventChecklist.js — setup steps for an event, and the reminder pop-up.  [marker: event-checklist v1]
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';

const GO_LABEL = {
  ticket_prices: 'Ticket types', promo_codes: 'Promo codes', catalog_items: 'Products', attendee_tag: 'Edit event',
  sponsorships: 'Sponsorship levels', confirmation: 'Emails', day_before: 'Emails', post_event: 'Emails', sponsor_email: 'Emails',
  links_tested: 'Open ticket page', name_tag_design: 'Design name tags', name_tags_ordered: null,
};

export function EventChecklist({ eventId, onGo, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const load = () => api.get(`/event-checklist/${eventId}`).then(r => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [eventId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const mark = async (key, status) => {
    try { const r = await api.post(`/event-checklist/${eventId}/${key}`, { status }); setData(r.data); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not update.'); }
  };
  const all = data.done === data.total;
  const pct = Math.round((100 * data.done) / data.total);
  return (
    <div className="card" style={{ marginBottom: 20, borderColor: all ? 'var(--gray-200)' : '#e0b44c' }}>
      <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span className="card-title">Event setup checklist</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.82rem', color: all ? '#1d7a3a' : '#7a5200' }}>
          <span style={{ width: 120, height: 8, background: 'var(--gray-100)', borderRadius: 4, overflow: 'hidden', display: 'inline-block' }}>
            <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: all ? '#2f9e58' : '#e0a92e' }} />
          </span>
          {all ? 'All set' : `${data.done} of ${data.total} done`} <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        </span>
      </div>
      {open && (
        <div className="card-body" style={{ padding: '4px 16px 10px' }}>
          {data.items.map(it => (
            <div key={it.key} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
              <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', fontSize: 13,
                background: it.state === 'done' ? '#2f9e58' : it.state === 'skipped' ? 'var(--gray-200)' : '#fff', color: it.state === 'todo' ? 'transparent' : '#fff',
                border: it.state === 'todo' ? '2px solid #e0b44c' : 'none' }}>{it.state === 'done' ? '✓' : it.state === 'skipped' ? '–' : ''}</span>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: '0.9rem', textDecoration: it.state === 'skipped' ? 'line-through' : 'none', color: it.state === 'skipped' ? 'var(--gray-500)' : 'inherit' }}>{it.label}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  {it.state === 'skipped' ? 'Not needed for this event' : it.note}
                  {it.marked && it.marked.by ? ` · ticked by ${it.marked.by}` : ''}
                </div>
              </div>
              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {GO_LABEL[it.key] && it.state === 'todo' && <button type="button" className="btn btn-secondary btn-sm" onClick={() => onGo(it.key)}>{GO_LABEL[it.key]}</button>}
                {it.state === 'todo' && !it.auto_done && <button type="button" className="btn btn-ghost btn-sm" onClick={() => mark(it.key, 'done')}>Mark done</button>}
                {it.state === 'todo' && it.skippable && <button type="button" className="btn btn-ghost btn-sm" onClick={() => mark(it.key, 'skipped')}>Not needed</button>}
                {it.marked && it.state !== 'todo' && !it.auto_done && <button type="button" className="btn btn-ghost btn-sm" onClick={() => mark(it.key, null)}>Undo</button>}
                {it.marked && it.marked.status === 'skipped' && it.auto_done && <button type="button" className="btn btn-ghost btn-sm" onClick={() => mark(it.key, null)}>Undo</button>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Pop-up after login: events in the next 3 weeks with steps still to do. Once per browser session.
export function EventChecklistReminder() {
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem('njjba-event-checklist-seen') === '1'; } catch (_) { /* ignore */ }
    if (seen) return;
    const t = setTimeout(() => {
      api.get('/event-checklist/upcoming').then(r => {
        const list = r.data.events || [];
        if (list.length) setEvents(list);
        try { sessionStorage.setItem('njjba-event-checklist-seen', '1'); } catch (_) { /* ignore */ }
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, []);
  if (!events.length) return null;
  const close = () => setEvents([]);
  const days = n => (n === 0 ? 'today' : n === 1 ? 'tomorrow' : `in ${n} days`);
  const date = d => { const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : d; };
  return (
    <div className="modal-overlay" onClick={close} style={{ zIndex: 999 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header"><span className="modal-title">Upcoming events still need setup</span><button className="btn btn-ghost btn-icon" onClick={close}>✕</button></div>
        <div className="modal-body" style={{ display: 'grid', gap: 14 }}>
          {events.map(e => (
            <div key={e.id} style={{ borderLeft: `3px solid ${e.days_away <= 7 ? 'var(--danger)' : '#e0a92e'}`, paddingLeft: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <strong>{e.name}</strong>
                <span style={{ fontSize: '0.78rem', color: e.days_away <= 7 ? 'var(--danger)' : 'var(--gray-500)' }}>{date(e.event_date)} · {days(e.days_away)}</span>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', margin: '2px 0 6px' }}>{e.done} of {e.total} steps done. Still to do:</div>
              <ul style={{ margin: '0 0 6px', paddingLeft: 18, fontSize: '0.85rem' }}>
                {e.missing.slice(0, 6).map(m => <li key={m}>{m}</li>)}
                {e.missing.length > 6 && <li>and {e.missing.length - 6} more</li>}
              </ul>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { close(); navigate(`/events/${e.id}`); }}>Open the checklist</button>
            </div>
          ))}
        </div>
        <div className="modal-footer"><button className="btn btn-primary" onClick={close}>Remind me next time</button></div>
      </div>
    </div>
  );
}
